import fs from 'fs';
import path from 'path';
import winston from 'winston';

import { IConfig } from './configs';
import MqttTransport from './mqttTransport';

const env = process.env.NODE_ENV || 'production';

export const singletonEnforcer = Symbol('singletonEnforcer');

export default class Logger {
  public static init(configs: IConfig): Promise<Logger> {
    return Logger.instance.init(configs);
  }

  public static log(level: string, message: any, ...meta: any): void {
    Logger.instance.getLogger().log({ level, message, meta });
  }

  public static error(message: any, ...meta: any): void {
    Logger.instance.getLogger().error(message, { meta });
  }

  public static warn(message: any, ...meta: any): void {
    Logger.instance.getLogger().warn(message, { meta });
  }

  public static info(message: any, ...meta: any): void {
    Logger.instance.getLogger().info(message, { meta });
  }

  public static verbose(message: any, ...meta: any): void {
    Logger.instance.getLogger().verbose(message, { meta });
  }

  public static debug(message: any, ...meta: any): void {
    Logger.instance.getLogger().debug(message, { meta });
  }

  public static silly(message: any, ...meta: any): void {
    Logger.instance.getLogger().silly(message, { meta });
  }

  public static logger(): Logger {
    return Logger.instance;
  }

  public static destroy(): void {
    Logger.singleton = undefined;
  }

  public static setLogger(logger: winston.Logger): void {
    Logger.instance.setLogger(logger);
  }

  private prefix: string;

  private defaultLogger: winston.Logger;

  private defaultTransports: any[];

  private transports: any[];

  private logLevel: string;

  private logsPath: string;

  private logFilename: string;

  private logMaxFilesize: number;

  private logMaxFiles: number;

  private topic: string;

  protected static singleton: Logger;

  constructor(enforcer: symbol) {
    if (enforcer !== singletonEnforcer) { throw new Error('Cannot construct singleton'); }

    const applicationPath = process.cwd();
    const logsPath = path.join(applicationPath, 'logs');

    this.prefix = '';
    this.logLevel = env === 'development' ? 'debug' : 'error';
    this.logsPath = logsPath;
    this.logFilename = 'application.log';
    this.logMaxFilesize = 20971520; // size in bytes
    this.logMaxFiles = 7;
    this.topic = 'logs';

    const rest = (info: any) => {
      const br = '\n'; // tslint:disable-line quotemark
      return (info.meta && (info.meta.length > 0)) ? ` - ${info.meta.map((m: any) => JSON.stringify(m, undefined, 2)).join(br)}` : '';
    };

    this.defaultTransports = [
      new winston.transports.Console({
        level: this.logLevel,
        format: winston.format.combine(
          // flowlint-line-ignore
          winston.format.colorize({ all: true }),
          // winston.format.splat(),
          winston.format.printf((info: winston.Logform.TransformableInfo) => {
            return `${(info as unknown as Record<string, unknown>).timestamp} - ${info.level}: ${this.prefix}${info.message}${rest(info)}`;
          })
        ),
      }),
    ];

    this.transports = this.defaultTransports;

    this.getLogger();
  }

  static get instance(): Logger {
    if (!Logger.singleton) {
      Logger.singleton = new Logger(singletonEnforcer);
    }
    return Logger.singleton;
  }

  static set instance(instance: Logger) {
    Logger.singleton = instance;
  }

  public init = async (configs: IConfig): Promise<Logger> => {
    const { logger, app } = configs;
    const { logMaxFilesize, logMaxFiles, logFilename, level, prefix, transports, topic, relativePath } = logger;

    this.prefix = `${prefix || app.name} - `;
    this.topic = topic || this.topic;
    this.logLevel = process.env.LOG_LEVEL || level || this.logLevel;
    this.logsPath = path.join(app.dataPath, relativePath) || this.logsPath;
    this.logMaxFilesize = logMaxFilesize || this.logMaxFilesize;
    this.logMaxFiles = logMaxFiles || this.logMaxFiles;
    this.logFilename = logFilename || this.logFilename;

    const loggerTransports = [...this.defaultTransports];
    transports.forEach((transport) => {
      switch (transport) {
        case 'console':
          break;
        case 'file': {
          const opts = {
            level: this.logLevel,
            filename: path.join(this.logsPath, this.logFilename),
            maxsize: this.logMaxFilesize,
            maxFiles: this.logMaxFiles,
            format: winston.format.combine(
              winston.format((info) => { return { ...info, pid: process.pid }; })(),
              winston.format.timestamp(),
              winston.format.json()
            ),
          };
          loggerTransports.push(new winston.transports.File(opts));
          break;
        }
        case 'broker': {
          loggerTransports.push(new MqttTransport({ topic: this.topic, level: this.logLevel, }));
          break;
        }
        default:
          // eslint-disable-next-line no-console
          console.warn(`Unexpected logger transport: ${transport}`);
          break;
      }
    });
    this.transports = loggerTransports;
    this.setLogLevel(this.logLevel);
    if (this.transports.includes('file')) {
      await this.initLogsFolders();
    }

    this.getLogger({ update: true }).silly('logs folder initialized');
    return this;
  }

  public getLogger = (params?: { update?: boolean }): winston.Logger => {
    const { transports } = this;
    if (!this.defaultLogger || (params && params.update)) {
      this.defaultLogger = winston.createLogger({
        format: winston.format.combine(
          winston.format.timestamp(),
          winston.format.colorize(),
          winston.format.simple()
        ),
        transports,
      });
      this.transports = transports;
    }
    return this.defaultLogger;
  }

  public setLogger = (logger: winston.Logger): void => {
    this.defaultLogger = logger;
  }

  public setLogLevel = (level: string) => {
    for (let i = 0; i < this.transports.length; i += 1) { // tslint:disable-line prefer-for-of
      this.transports[i].level = level;
    }
  }

  public getLogLevel = (): string => {
    return (this.transports.length > 0) ? this.transports[0].level : 'n.d.';
  }

  public setPrefix = (prefix: string) => {
    this.prefix = prefix;
  }

  public getTransports = (): winston.transport[] => {
    return this.getLogger().transports;
  }

  public defaultListener = (topic: string, message: any = {}): Promise<void> => {
    const messageKeys = Object.keys(message);
    for (let index = 0; index < messageKeys.length; index += 1) {
      const key = messageKeys[index];
      const value = message[key];
      switch (key) {
        case 'level':
          this.setLogLevel(value);
          break;
        default:
          break;
      }
    }
    return Promise.resolve();
  }

  private initLogsFolders = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      try {
        fs.mkdirSync(this.logsPath);
        this.getLogger().info(`created logs folder: ${this.logsPath}`);
      } catch (e) {
        const error = e as Record<string, unknown>;
        if (error.code !== 'EEXIST') {
          // eslint-disable-next-line no-console
          console.error(`ERROR: error creating logs folder ${this.logsPath}`);
          reject(e);
        }
      }
      resolve();
    });
  }
}
